import { useState } from 'react';
import { ShieldCheck, ShieldAlert } from 'lucide-react';
import { MutationButton } from '@/components/ui/mutation-button.js';
import { AIReasoningStepper, useAIStepperState } from '@/components/ui/ai-stepper.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { cn } from '@/lib/utils.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '@edison/api-client-react';
import type { AIStep } from '@/components/ui/ai-stepper.js';

const ELIGIBILITY_STEPS = [
  { key: 'score', label: 'Scoring gateway criteria' },
  { key: 'cots', label: 'Running COTS sweep' },
  { key: 'signals', label: 'Reading signals' },
  { key: 'derive', label: 'Deriving status' },
];

type EligibilityStatus = 'intact' | 'weakening' | 'at_risk';

interface Snapshot {
  id: string;
  status: EligibilityStatus;
  rationale: string;
  createdAt: string;
}

interface LedgerPayload {
  current: Snapshot | null;
  history: Snapshot[];
}

const STATUS_CONFIGS: Record<EligibilityStatus, { label: string; bg: string; fg: string; Icon: typeof ShieldCheck }> = {
  intact:    { label: 'Intact',    bg: 'bg-[oklch(0.95_0.03_155)]', fg: 'text-[oklch(0.40_0.11_155)]', Icon: ShieldCheck },
  weakening: { label: 'Weakening', bg: 'bg-[oklch(0.96_0.05_80)]',  fg: 'text-[oklch(0.50_0.11_72)]',  Icon: ShieldAlert },
  at_risk:   { label: 'At risk',   bg: 'bg-[oklch(0.95_0.03_25)]',  fg: 'text-[oklch(0.55_0.16_25)]',  Icon: ShieldAlert },
};

function StatusHero({ status, rationale }: { status: EligibilityStatus; rationale: string }) {
  const cfg = STATUS_CONFIGS[status];
  const Icon = cfg.Icon;
  return (
    <div className={cn('rounded-[14px] px-6 py-5 flex items-start gap-4', cfg.bg)}>
      <Icon className={cn('h-6 w-6 shrink-0 mt-0.5', cfg.fg)} aria-hidden="true" />
      <div>
        <p className={cn('text-sm font-semibold mb-1', cfg.fg)}>
          Eligibility status: {cfg.label}
        </p>
        <p className="text-sm text-foreground leading-snug">{rationale}</p>
      </div>
    </div>
  );
}

interface EligibilityTabProps {
  projectId: string;
}

export function EligibilityTab({ projectId }: EligibilityTabProps) {
  const qc = useQueryClient();
  const [runSteps, setRunSteps] = useState<AIStep[] | null>(null);
  const stepHelpers = useAIStepperState(ELIGIBILITY_STEPS);

  const { data, isLoading, error, refetch } = useQuery<{ data: LedgerPayload }>({
    queryKey: ['eligibility-ledger', projectId],
    queryFn: () => customInstance({ url: `/api/projects/${projectId}/eligibility-ledger`, method: 'GET' }),
  });

  const runMutation = useMutation({
    mutationFn: async () => {
      const delay = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

      let steps = stepHelpers.advanceTo(stepHelpers.initial, 'score');
      setRunSteps([...steps]);

      await delay(600);
      steps = stepHelpers.advanceTo(steps, 'cots');
      setRunSteps([...steps]);

      await delay(600);
      steps = stepHelpers.advanceTo(steps, 'signals');
      setRunSteps([...steps]);

      await delay(600);
      steps = stepHelpers.advanceTo(steps, 'derive');
      setRunSteps([...steps]);

      await customInstance({ url: `/api/projects/${projectId}/eligibility/run`, method: 'POST' });

      steps = stepHelpers.completeAll(steps);
      setRunSteps([...steps]);
      await delay(500);
      setRunSteps(null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['eligibility-ledger', projectId] });
    },
    onError: () => {
      setRunSteps(prev => (prev ? stepHelpers.errorAt(prev, 'derive') : null));
    },
  });

  const ledger = data?.data;
  const latest = ledger?.current;
  const history = ledger?.history ?? [];

  const buttonState = runMutation.isPending
    ? 'saving'
    : runMutation.isSuccess && runSteps === null
    ? 'saved'
    : 'idle';

  return (
    <div className="space-y-6">
      {isLoading ? (
        <Skeleton className="h-24 rounded-[14px]" />
      ) : error ? (
        <InlineError message="Failed to load eligibility status." onRetry={() => refetch()} />
      ) : latest ? (
        <StatusHero status={latest.status} rationale={latest.rationale} />
      ) : (
        <div className="rounded-[14px] border border-border bg-card px-6 py-5 text-sm text-muted-foreground">
          No eligibility assessment yet. Run the check below to get started.
        </div>
      )}

      <div className="flex items-start gap-4 flex-wrap">
        <MutationButton
          state={buttonState}
          idleLabel="Re-run check now"
          savingLabel="Running…"
          savedLabel="Done"
          onClick={() => runMutation.mutate()}
          disabled={runMutation.isPending}
          variant="secondary"
        />
        {runSteps && <AIReasoningStepper steps={runSteps} className="flex-1 min-w-0" />}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Assessment history</h3>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 rounded-[10px]" />)}
          </div>
        ) : history.length === 0 ? (
          <EmptyState
            title="No eligibility snapshots yet"
            body="Run the eligibility check to capture the first snapshot. Snapshots are also created automatically on the monthly review cycle."
          />
        ) : (
          <div className="rounded-[14px] border border-border bg-card overflow-hidden max-h-[480px] overflow-y-auto">
            {history.map((snap, i) => {
              const cfg = STATUS_CONFIGS[snap.status];
              return (
                <div key={snap.id} className={cn('px-5 py-4', i !== 0 && 'border-t border-border')}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={cn('inline-flex items-center gap-1.5 text-xs font-semibold', cfg.fg)}>
                      <cfg.Icon className="h-3.5 w-3.5" aria-hidden="true" />
                      {cfg.label}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {new Date(snap.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <p className="text-sm text-foreground leading-snug">{snap.rationale}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
